import React, { useEffect, useMemo, useState } from 'react';

const formatFlightDate = (datetime) =>
  new Date(datetime).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: '2-digit',
  }).replace(',', '');

const formatFlightTime = (datetime) =>
  new Date(datetime).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

const getLegLabel = (routeType, index, total) => {
  const normalized = String(routeType || '').toLowerCase();
  if (normalized === 'return' || normalized === 'round_trip') {
    if (index === 0) return 'Outbound';
    if (index === 1) return 'Return';
  }
  if (normalized === 'multi_city' && total > 1) {
    return `Leg ${index + 1}`;
  }
  return total > 1 ? `Flight ${index + 1}` : 'Flight';
};

const getLegChangeBlockReason = (flight, groupAlreadyChanged = false) => {
  const departure = flight?.departure?.datetime;
  if (!departure) return null;

  const hoursToDeparture =
    (new Date(departure).getTime() - Date.now()) / (1000 * 60 * 60);

  if (groupAlreadyChanged || flight.is_flight_changed || flight.parent_booking_id) {
    return 'This booking has already been changed once.';
  }

  if (flight.booking_status && !['Upcoming', 'Confirmed'].includes(flight.booking_status)) {
    return `This leg cannot be changed (${flight.booking_status}).`;
  }

  if (hoursToDeparture <= 1) {
    return 'Cannot change within 1 hour of departure.';
  }

  return null;
};

const getFlightKey = (flight, index) =>
  flight.booking_token || flight.id || `leg-${index}`;

const FlightLegCard = ({ flight, index, routeType, totalLegs, selected, disabled, blockReason, onToggle }) => (
  <label
    className={`block rounded-lg border p-4 transition-colors cursor-pointer ${
      disabled
        ? 'border-gray-200 bg-gray-50 opacity-70 cursor-not-allowed'
        : selected
          ? 'border-jet-yellow bg-yellow-50/80 ring-1 ring-jet-yellow'
          : 'border-gray-200 bg-white hover:border-yellow-300 hover:bg-yellow-50/40'
    }`}
  >
    <div className="flex items-start gap-3">
      <input
        type="checkbox"
        checked={selected}
        disabled={disabled}
        onChange={() => !disabled && onToggle()}
        className="mt-1 h-4 w-4 rounded border-gray-300 text-jet-yellow focus:ring-jet-yellow disabled:cursor-not-allowed"
      />

      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="text-xs font-bold uppercase tracking-wide text-[#8B3D3D]">
            {getLegLabel(routeType, index, totalLegs)}
          </span>
          <span className="text-xs font-bold text-gray-700">{flight.flight_number}</span>
          {flight.booking_status && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
              {flight.booking_status}
            </span>
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
          <div>
            <div className="text-sm font-bold text-gray-900">
              {flight.departure?.iata}{' '}
              {formatFlightTime(flight.departure?.datetime)}
            </div>
            <div className="text-[11px] text-gray-500 mt-0.5">
              {flight.departure?.airport || flight.departure?.iata}
            </div>
            <div className="text-[11px] text-gray-500">
              {formatFlightDate(flight.departure?.datetime)}
            </div>
          </div>

          <div className="hidden sm:flex flex-col items-center px-2 text-gray-400">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
            {flight.duration_minutes != null && (
              <span className="text-[10px] font-bold uppercase mt-1">
                {Math.floor(flight.duration_minutes / 60)}h {flight.duration_minutes % 60}m
              </span>
            )}
          </div>

          <div>
            <div className="text-sm font-bold text-gray-900">
              {flight.arrival?.iata}{' '}
              {formatFlightTime(flight.arrival?.datetime)}
            </div>
            <div className="text-[11px] text-gray-500 mt-0.5">
              {flight.arrival?.airport || flight.arrival?.iata}
            </div>
            <div className="text-[11px] text-gray-500">
              {formatFlightDate(flight.arrival?.datetime)}
            </div>
          </div>
        </div>

        {blockReason && (
          <p className="mt-2 text-xs text-amber-700 font-medium">{blockReason}</p>
        )}
      </div>
    </div>
  </label>
);

/**
 * Modal for choosing which booking leg(s) to change on return / multi-city bookings.
 */
const ChangeFlightLegSelectionModal = ({
  isOpen,
  onClose,
  onConfirm,
  flights = [],
  routeType = '',
  referenceNumber = '',
  groupAlreadyChanged = false,
}) => {
  const [selectedKeys, setSelectedKeys] = useState(new Set());

  const legMeta = useMemo(
    () =>
      flights.map((flight, index) => {
        const key = getFlightKey(flight, index);
        const blockReason = getLegChangeBlockReason(flight, groupAlreadyChanged);
        return {
          flight,
          index,
          key,
          blockReason,
          changeable: !blockReason,
        };
      }),
    [flights, groupAlreadyChanged]
  );

  const changeableLegs = legMeta.filter((leg) => leg.changeable);
  const changeableKeys = changeableLegs.map((leg) => leg.key);

  useEffect(() => {
    if (!isOpen) {
      setSelectedKeys(new Set());
      return;
    }

    if (changeableKeys.length === 1) {
      setSelectedKeys(new Set(changeableKeys));
    }
  }, [isOpen, changeableKeys.join('|')]);

  if (!isOpen) return null;

  const allChangeableSelected =
    changeableKeys.length > 0 &&
    changeableKeys.every((key) => selectedKeys.has(key));

  const someChangeableSelected = changeableKeys.some((key) => selectedKeys.has(key));

  const handleToggleLeg = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleToggleAll = () => {
    if (allChangeableSelected) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(changeableKeys));
    }
  };

  const handleConfirm = () => {
    const selectedFlights = legMeta
      .filter((leg) => selectedKeys.has(leg.key))
      .map(({ flight, index, key }) => ({
        ...flight,
        legIndex: index,
        legLabel: getLegLabel(routeType, index, flights.length),
        selectionKey: key,
      }));

    if (selectedFlights.length === 0) return;
    onConfirm?.(selectedFlights);
  };

  const routeLabel = (() => {
    const normalized = String(routeType || '').toLowerCase();
    if (normalized === 'multi_city') return 'Multi-City';
    if (normalized === 'return' || normalized === 'round_trip') return 'Return';
    return 'Multi-Leg';
  })();

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-flight-legs-title"
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
      >
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 id="change-flight-legs-title" className="text-lg font-bold text-gray-900">
                Select flight(s) to change
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {routeLabel} booking{referenceNumber ? ` · ${referenceNumber}` : ''}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Choose one or more flights you want to change. You can select all legs or only specific segments.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl leading-none shrink-0"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {changeableLegs.length > 1 && (
            <label className="flex items-center gap-3 rounded-lg border border-dashed border-gray-300 px-4 py-3 cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={allChangeableSelected}
                ref={(input) => {
                  if (input) {
                    input.indeterminate = someChangeableSelected && !allChangeableSelected;
                  }
                }}
                onChange={handleToggleAll}
                className="h-4 w-4 rounded border-gray-300 text-jet-yellow focus:ring-jet-yellow"
              />
              <span className="text-sm font-bold text-gray-900">Select all changeable flights</span>
              <span className="text-xs text-gray-500">
                ({changeableLegs.length} available)
              </span>
            </label>
          )}

          {legMeta.map(({ flight, index, key, blockReason, changeable }) => (
            <FlightLegCard
              key={key}
              flight={flight}
              index={index}
              routeType={routeType}
              totalLegs={flights.length}
              selected={selectedKeys.has(key)}
              disabled={!changeable}
              blockReason={blockReason}
              onToggle={() => handleToggleLeg(key)}
            />
          ))}

          {changeableLegs.length === 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              None of the flights in this booking can be changed right now.
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto px-6 py-2.5 text-sm font-bold text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={selectedKeys.size === 0}
            className="w-full sm:w-auto px-6 py-2.5 text-sm font-bold bg-jet-yellow text-gray-900 rounded-md hover:bg-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            Continue{selectedKeys.size > 0 ? ` (${selectedKeys.size} selected)` : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangeFlightLegSelectionModal;
